'use client';

/**
 * StepNode — a React Flow node for one sequence action (spec §5 node anatomy).
 *
 * Renders: channel icon · action + type · delay · AI badge (when the copy has
 * {{ai}} blocks) · approval badge · live status (reached/sent once the campaign
 * is running). Warm-gray
 * surface; coral only accents the selected node. Hover reveals node actions
 * (insert-after, add branch, duplicate, remove) — the layout re-runs after each,
 * so the recruiter never drags edges. Used for email / linkedin_* / manual_task;
 * `branch` nodes render via ConditionNode.
 */

import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import {
    Mail,
    UserPlus,
    MessageSquare,
    MessageCircle,
    ClipboardList,
    Clock,
    ShieldCheck,
    Plus,
    GitBranch,
    Copy,
    Trash2,
    Sparkles,
    Send,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { StepNode as StepNodeModel, StepType } from '../types';
import { STEP_TYPE_META, nodeHasAiBlocks } from '../sequence-tree';

export type StepNodeStat = { reached: number; sent: number; replies?: number };

export type SequenceStepNodeData = {
    node: StepNodeModel;
    selected: boolean;
    stat?: StepNodeStat | null;
    accountLabel?: string | null;
    onSelect: (id: string) => void;
    /** Opens the step-type picker anchored at the click; inserts on the linear slot. */
    onInsertAfter: (e: React.MouseEvent, id: string) => void;
    onAddBranch: (id: string) => void;
    onDuplicate: (id: string) => void;
    onRemove: (id: string) => void;
    canRemove: boolean;
};

const STEP_ICON: Record<Exclude<StepType, 'branch'>, LucideIcon> = {
    email: Mail,
    linkedin_invite: UserPlus,
    linkedin_message: MessageSquare,
    linkedin_inmail: Send,
    whatsapp_message: MessageCircle,
    manual_task: ClipboardList,
};

const WARM_GRAY = '#C8C3BA';
const WARM_GRAY_BG = 'rgba(200, 195, 186, 0.22)';

function delayLabel(node: StepNodeModel): string {
    const v = Number(node.delay?.value) || 0;
    if (v <= 0) return 'Immediately';
    const unit = v === 1 ? node.delay.unit.replace(/s$/, '') : node.delay.unit;
    return `Wait ${v} ${unit}`;
}

function StepNodeInner({ data }: NodeProps) {
    const d = data as unknown as SequenceStepNodeData;
    const { node, selected } = d;
    const meta = STEP_TYPE_META[node.type];
    const Icon = STEP_ICON[node.type as Exclude<StepType, 'branch'>] ?? Mail;
    // Derived, not a mode: the copy contains {{ai}} blocks → it's an AI step.
    const isAiStep = node.type !== 'manual_task' && nodeHasAiBlocks(node);

    const stop = (e: React.MouseEvent) => e.stopPropagation();

    return (
        <div
            onClick={() => d.onSelect(node.id)}
            className="group relative flex cursor-pointer flex-col gap-2 px-3.5 py-3 transition-all"
            style={{
                width: 256,
                minHeight: 108,
                borderRadius: 14,
                background: 'var(--surface-primary)',
                border: `${selected ? 2 : 1.5}px solid ${selected ? 'var(--accent)' : 'var(--border-default)'}`,
                boxShadow: selected
                    ? '0 0 0 3px rgba(253,119,94,0.15), 0 2px 8px rgba(120,90,60,0.08)'
                    : '0 1px 3px rgba(120,90,60,0.06), 0 4px 12px rgba(120,90,60,0.04)',
            }}
        >
            <Handle
                type="target"
                position={Position.Top}
                style={{ width: 8, height: 8, background: WARM_GRAY, border: '2px solid white', top: -5 }}
            />

            {/* header: icon + action */}
            <div className="flex items-center gap-2.5">
                <div
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                    style={{
                        background: selected ? 'var(--accent-muted)' : WARM_GRAY_BG,
                        color: selected ? 'var(--accent-emphasis)' : 'var(--text-secondary)',
                    }}
                >
                    <Icon size={16} strokeWidth={1.75} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {meta.label}
                    </div>
                    <div className="flex items-center gap-1 text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                        <Clock size={11} strokeWidth={1.75} />
                        {delayLabel(node)}
                    </div>
                </div>
            </div>

            {/* badges row */}
            <div className="flex flex-wrap items-center gap-1.5">
                {isAiStep && (
                    <MiniBadge tone="accent">
                        <Sparkles size={10} strokeWidth={2} /> AI
                    </MiniBadge>
                )}
                {node.approvalRequired && (
                    <MiniBadge tone="accent">
                        <ShieldCheck size={10} strokeWidth={2} /> Approval
                    </MiniBadge>
                )}
                {d.accountLabel && <MiniBadge tone="muted">{d.accountLabel}</MiniBadge>}
                {d.stat && d.stat.reached > 0 && (
                    <MiniBadge tone="success">
                        {d.stat.sent}/{d.stat.reached} sent
                    </MiniBadge>
                )}
                {d.stat && (d.stat.replies ?? 0) > 0 && (
                    <MiniBadge tone="accent">
                        <MessageSquare size={10} strokeWidth={2} /> {d.stat.replies}{' '}
                        {d.stat.replies === 1 ? 'reply' : 'replies'}
                    </MiniBadge>
                )}
            </div>

            {/* hover toolbar — top-right */}
            <div
                className="absolute -top-3 right-2 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                style={{ pointerEvents: 'auto' }}
            >
                <NodeActionBtn
                    label="Duplicate step"
                    onClick={(e) => {
                        stop(e);
                        d.onDuplicate(node.id);
                    }}
                >
                    <Copy size={12} strokeWidth={2} />
                </NodeActionBtn>
                <NodeActionBtn
                    label="Remove step"
                    danger
                    disabled={!d.canRemove}
                    onClick={(e) => {
                        stop(e);
                        if (d.canRemove) d.onRemove(node.id);
                    }}
                >
                    <Trash2 size={12} strokeWidth={2} />
                </NodeActionBtn>
            </div>

            <Handle
                type="source"
                position={Position.Bottom}
                style={{ width: 8, height: 8, background: WARM_GRAY, border: '2px solid white', bottom: -5 }}
            />

            {/* add affordances — bottom center, hover-revealed */}
            <div
                className="absolute -bottom-3.5 left-1/2 flex -translate-x-1/2 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                style={{ pointerEvents: 'auto' }}
            >
                <NodeActionBtn
                    label="Add step after"
                    onClick={(e) => {
                        stop(e);
                        d.onInsertAfter(e, node.id);
                    }}
                >
                    <Plus size={12} strokeWidth={2.25} />
                </NodeActionBtn>
                <NodeActionBtn
                    label="Add branch after"
                    onClick={(e) => {
                        stop(e);
                        d.onAddBranch(node.id);
                    }}
                >
                    <GitBranch size={12} strokeWidth={2} />
                </NodeActionBtn>
            </div>
        </div>
    );
}

function MiniBadge({
    children,
    tone = 'default',
}: {
    children: React.ReactNode;
    tone?: 'default' | 'accent' | 'muted' | 'success';
}) {
    const tones = {
        default: { bg: 'var(--surface-tertiary)', color: 'var(--text-secondary)' },
        accent: { bg: 'var(--accent-muted)', color: 'var(--accent-emphasis)' },
        muted: { bg: 'var(--surface-secondary)', color: 'var(--text-tertiary)' },
        success: { bg: 'var(--bg-success)', color: 'var(--fg-success)' },
    }[tone];
    return (
        <span
            className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-medium"
            style={{ background: tones.bg, color: tones.color }}
        >
            {children}
        </span>
    );
}

function NodeActionBtn({
    children,
    label,
    onClick,
    danger,
    disabled,
}: {
    children: React.ReactNode;
    label: string;
    onClick: (e: React.MouseEvent) => void;
    danger?: boolean;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            aria-label={label}
            title={label}
            disabled={disabled}
            onClick={onClick}
            className="nodrag nopan flex h-6 w-6 items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-30"
            style={{
                background: 'var(--surface-primary)',
                border: '1px solid var(--border-emphasis)',
                color: danger ? 'var(--fg-danger)' : 'var(--text-secondary)',
                boxShadow: 'var(--shadow-sm)',
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = danger ? 'var(--bg-danger)' : 'var(--surface-hover)';
                e.currentTarget.style.borderColor = danger ? 'var(--fg-danger)' : 'var(--accent)';
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = 'var(--surface-primary)';
                e.currentTarget.style.borderColor = 'var(--border-emphasis)';
            }}
        >
            {children}
        </button>
    );
}

export const StepNode = memo(StepNodeInner);
